import { useMemo } from 'react'

import { DateTime } from 'luxon'

import {
  digObject,
  matchFilterArrayIncludes,
  matchFilterBetweenDates,
  matchFilterNumber,
  matchFilterString,
  sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { pendingStatusKeys } from '@functions/status'

import type { OrderItemModel, StatusModel } from '@models/types'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['orderItems', 'statuses', 'artworks']

type MatchStatusGroupKeyParams = {
  artworkStatus: StatusModel,
  orderItemStatus: StatusModel,
  statusKey: string,
}
const matchStatusGroupKey = (params: MatchStatusGroupKeyParams) => {
  const { artworkStatus, orderItemStatus, statusKey } = params

  if (statusKey === 'pending'){
    // @ts-expect-error TS2345
    return pendingStatusKeys.includes(orderItemStatus.key)
  }

  if (statusKey === 'complete'){
    // @ts-expect-error TS2345
    return !pendingStatusKeys.includes(orderItemStatus.key)
  }

  if (statusKey === 'artwork_approved'){
    return artworkStatus.key === 'approved'
  }

  if (statusKey === 'artwork_pending'){
    return !artworkStatus.key || artworkStatus.key === 'pending'
  }

  if (statusKey === 'not_pending'){
    return orderItemStatus.key !== 'pending'
  }

  return true
}

type OrderItemsFilters = {
  agency_id?: number,
  agent_id?: number,
  artwork_ids?: number[],
  campaignIsHidden?: boolean,
  campaign_id?: number,
  due_date_end?: string,
  due_date_start?: string,
  exclude_without_campaign?: boolean,
  order_id?: number,
  provider_id?: number,
  priority_order_items_by_user_id?: number,
  publication_id?: number,
  service_id?: number,
  status?: string,
  status_group_key?: 'all' | 'artwork_approved' | 'artwork_pending' | 'complete' | 'not_pending' | 'pending',
  taskable_only?: boolean,
  updated_date_end?: string,
  updated_date_start?: string,
}

type UseOrderItemsOptions = {
  filters?: OrderItemsFilters,
  performHttpRequests?: boolean,
  requestOptions?: { [key: string]: boolean | undefined },
  sortBy?: string,
  sortKey?: string,
}

function useOrderItems(options: UseOrderItemsOptions) {
  const {
    filters = {}, requestOptions, sortBy = 'id desc', sortKey,
  } = options
  const {
    agency_id: filterAgencyId,
    agent_id: filterAgentId,
    artwork_ids: filterArtworkIds,
    campaignIsHidden: filterCampaignIsHidden,
    campaign_id: filterCampaignId,
    due_date_end: filterDueDateEnd,
    due_date_start: filterDueDateStart,
    exclude_without_campaign: filterExcludeWithoutCampaign,
    order_id: filterOrderId,
    priority_order_items_by_user_id: filterByPriorityCampaignsUserId,
    provider_id: filterProviderId,
    publication_id: filterPublicationId,
    service_id: filterServiceId,
    status: filterStatusKey,
    status_group_key: filterStatusGroupKey,
    taskable_only: filterIsTaskableOnly,
    updated_date_end: filterUpdatedDateEnd,
    updated_date_start: filterUpdatedDateStart,
  } = filters

  const {
    updatedEntities: {
      artworks: artworksUpdatedAt,
      campaigns: campaignsUpdatedAt,
      orderItems: orderItemsUpdatedAt,
      providers: providersUpdatedAt,
      publications: publicationsUpdatedAt,
      services: servicesUpdatedAt,
      statuses: statusesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    artworks, campaigns, orderItems, orders, services, statuses,
  } = useSelector(reduxState => reduxState.entities)

  const filteredOrderItems = useMemo(() => {
    const filtered = Object.values(orderItems).filter((orderItem: OrderItemModel) => {
      const {
        agent_ids = [], artwork_id, order_id, priority_campaign_user_ids = [], status_id, service_id,
      } = orderItem

      const artwork = artwork_id && artworks[artwork_id] ? artworks[artwork_id] : {}
      // @ts-expect-error TS2339
      const artworkStatus = artwork && statuses[artwork.status_id] ? statuses[artwork.status_id] : {}

      const order = order_id && orders[order_id] ? orders[order_id] : {}
      const status = status_id && statuses[status_id] ? statuses[status_id] : {}
      const service = service_id && services[service_id] ? services[service_id] : {}
      const taskable = digObject(service, 'options.taskable')

      // @ts-expect-error TS2339
      const agencyId = orderItem.agency_id || order.agency_id

      // @ts-expect-error TS2339
      const campaignId = orderItem.campaign_id || order.campaign_id
      const campaign = campaignId && campaigns[campaignId] ? campaigns[campaignId] : {}

      // add exact match here once merged
      // @ts-expect-error TS2339
      const matchCampaignHidden = matchFilterString(campaign.hidden, filterCampaignIsHidden)

      const matchAgency = matchFilterNumber(agencyId, filterAgencyId)
      const matchAgents = filterAgentId ? matchFilterArrayIncludes(agent_ids, Number(filterAgentId)) : true

      const matchArtworkId = filterArtworkIds ? matchFilterArrayIncludes(filterArtworkIds, artwork_id || -1) : true

      const matchCampaignId = matchFilterNumber(campaignId, filterCampaignId)
      const matchOrderId = matchFilterNumber(order_id, filterOrderId)
      // @ts-expect-error TS2339
      const matchProviderId = matchFilterNumber(order?.provider_id, filterProviderId)
      // @ts-expect-error TS2339
      const matchPublicationId = matchFilterNumber(order?.publication_id, filterPublicationId)
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)

      const matchIsTaskable = filterIsTaskableOnly ? taskable === true : true
      // @ts-expect-error TS2339
      const matchStatusKey = matchFilterString(status.key, filterStatusKey, { exact: true })
      const matchExcludeCampaign = filterExcludeWithoutCampaign ? !!campaignId : true

      // match due date
      const dueDate = digObject(orderItem, 'dates.due.date', '')
      const dueDateFormatted = DateTime.fromFormat(dueDate, 'dd/MM/yyyy').toFormat('yyyy-MM-dd')
      const matchDueDate = matchFilterBetweenDates(dueDateFormatted, filterDueDateStart, filterDueDateEnd)

      // match updated date
      const updatedDate = digObject(orderItem, 'dates.updated.date_time', '')
      const updatedDateFormatted = DateTime.fromFormat(updatedDate, 'dd/MM/yyyy HH:mm').toFormat('yyyy-MM-dd')
      const matchUpdatedDate = matchFilterBetweenDates(
        updatedDateFormatted,
        filterUpdatedDateStart,
        filterUpdatedDateEnd,
      )

      const matchPriorityCampaigns = filterByPriorityCampaignsUserId
        ? matchFilterArrayIncludes(priority_campaign_user_ids, Number(filterByPriorityCampaignsUserId)) : true

      const matchesStatusGroupKey = matchStatusGroupKey({
        // @ts-expect-error TS2739
        artworkStatus,
        // @ts-expect-error TS2739
        orderItemStatus: status,
        // @ts-expect-error TS2322
        statusKey: filterStatusGroupKey,
      })

      return matchAgency
        && matchAgents
        && matchArtworkId
        && matchCampaignHidden
        && matchCampaignId
        && matchDueDate
        && matchExcludeCampaign
        && matchIsTaskable
        && matchOrderId
        && matchPriorityCampaigns
        && matchProviderId
        && matchPublicationId
        && matchServiceId
        && matchStatusKey
        && matchUpdatedDate
        && matchesStatusGroupKey
    })

    const [column, direction] = sortBy.split(' ')

    // @ts-expect-error TS2345
    const sorted = sortArrayBy(filtered, direction, column)

    return sorted
  }, [
    JSON.stringify(filters),
    artworksUpdatedAt,
    campaignsUpdatedAt,
    orderItemsUpdatedAt,
    providersUpdatedAt,
    publicationsUpdatedAt,
    servicesUpdatedAt,
    statusesUpdatedAt,
  ])

  const filteredOrderItemsCount = filteredOrderItems.length
  const hasFilteredOrderItems = !!filteredOrderItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredOrderItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrderItems } = useReduxAction(
    'orderItems',
    'loadOrderItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderItems,
    filteredOrderItemsCount,
    hasFilteredOrderItems,
    loading: loadingOrderItems,
  }
}

export type UseOrderItemsPayload = ReturnType<typeof useOrderItems>

export default useOrderItems
